import { useEffect } from 'react';

import { ApolloClient, ApolloProvider, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import { ReactRouterRoutingProvider } from '@bugsnag/react-router-performance';
import { CssBaseline } from '@mui/material';
import { LicenseInfo } from '@mui/x-license';
import posthog from 'posthog-js';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { RecoilRoot } from 'recoil';
import { navigate } from '@redwoodjs/router';
import { RedwoodProvider } from '@redwoodjs/web';
import { RedwoodApolloProvider } from '@redwoodjs/web/apollo';
import { Toaster } from '@redwoodjs/web/dist/toast';

import { ConfigProvider } from 'src/contexts/ConfigContext';
import NavigationScroll from 'src/layouts/NavigationScroll';
import FatalErrorPage from 'src/pages/FatalErrorPage';
import Routes from 'src/Routes';
import { store } from 'src/store';
import './index.css';
import './scaffold.css';

// #region Global Styles
import '@fontsource/material-icons';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
// #endregion Global Styles

import { version } from '../../package.json';
import { bugsnagConfig, ErrorBoundary, LogError } from '../src/lib/error-logger';

import { basename, routes } from './bugsnagRoutes';
import { ACCESS_TOKEN_KEY } from './constants';
import { AddPartnerProvider } from './contexts/AddPartnerProvider';
import { AppProvider } from './contexts/AppProvider';
import { AuthProvider } from './contexts/AuthProvider';
import { FeatureFlagProvider } from './contexts/FeatureFlagProvider';
import { NotificationsProvider } from './contexts/NotificationsProvider';
import i18n from './lib/i18n';
import ThemeCustomization from './themes';

LicenseInfo.setLicenseKey(
    '6124a5558e182b61b8ca2e473eb401ddTz0xMDAzODAsRT0xNzYxMjE1MTYwMDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI='
);

// #region Bugsnag Configuration

export const bugsnagClient = Bugsnag.createClient(bugsnagConfig);

Bugsnag.start(bugsnagConfig);

BugsnagPerformance.start({
    apiKey: `${process.env.FRONTEND_BUGSNAG_API_KEY}`,
    appVersion: version,
    bugsnag: Bugsnag,
    releaseStage: `${process.env.STAGE}`,
    enabledReleaseStages: ['prod', 'uat', 'dev'],
    routingProvider: new ReactRouterRoutingProvider(routes, basename),
});
// #endregion

const { POSTHOG_API_URL, POSTHOG_API_KEY } = process.env;
posthog.init(POSTHOG_API_KEY, { api_host: POSTHOG_API_URL });

const App = () => {
    const [_, setStoredAccessToken] = useLocalStorage<string>(ACCESS_TOKEN_KEY, null);
    const httpLink = createHttpLink({
        uri: `${window.RWJS_API_GRAPHQL_URL}`,
    });

    useEffect(() => {
        const unregisterServiceWorkers = async () => {
            try {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(
                    regs.map((r) => {
                        return r.unregister();
                    })
                );
            } catch (error) {
                // Try catch to prevent site from crashing if the unregister fails for whichever reason
                LogError(error);
            }
        };
        // Force unregister existing service workers
        unregisterServiceWorkers();
    }, []);

    const authLink = setContext((_, { headers }) => {
        const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        return {
            headers: {
                ...headers,
                Authorization: accessToken ? `Bearer ${JSON.parse(accessToken)}` : '',
            },
        };
    });

    const errorLink = onError(({ graphQLErrors }) => {
        if (graphQLErrors)
            graphQLErrors.forEach(({ message }) => {
                if (message === 'You need to be authenticated to perform the action.') {
                    setStoredAccessToken(null);
                    navigate('/login', { replace: true });
                }
            });
    });

    const client = new ApolloClient({
        link: errorLink.concat(authLink.concat(httpLink)),
        cache: new InMemoryCache(),
    });

    return (
        <ErrorBoundary FallbackComponent={FatalErrorPage}>
            <I18nextProvider i18n={i18n}>
                <RedwoodProvider titleTemplate="%PageTitle | %AppTitle">
                    <RecoilRoot>
                        <RedwoodApolloProvider>
                            <ApolloProvider client={client}>
                                <ThemeCustomization>
                                    <CssBaseline />
                                    <AppProvider>
                                        <Provider store={store}>
                                            <ConfigProvider>
                                                <BrowserRouter basename={basename}>
                                                    <NavigationScroll>
                                                        <AuthProvider>
                                                            <AddPartnerProvider>
                                                                <NotificationsProvider>
                                                                    <FeatureFlagProvider>
                                                                        <Routes />
                                                                    </FeatureFlagProvider>
                                                                </NotificationsProvider>
                                                            </AddPartnerProvider>
                                                        </AuthProvider>
                                                    </NavigationScroll>
                                                </BrowserRouter>
                                            </ConfigProvider>
                                        </Provider>
                                    </AppProvider>
                                    <Toaster
                                        toastOptions={{
                                            className: 'rw-toast',
                                            duration: 6000,
                                            position: 'bottom-center',
                                        }}
                                    />
                                </ThemeCustomization>
                            </ApolloProvider>
                        </RedwoodApolloProvider>
                    </RecoilRoot>
                </RedwoodProvider>
            </I18nextProvider>
        </ErrorBoundary>
    );
};

export default App;
